import SeoFooter from "../../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Plane, MapPin, Clock, Shield, Users, Car, ChevronRight, Phone, Mail, Globe, Star,
  CheckCircle2, Briefcase, Heart, MapPinned, Wifi, ChevronDown, Quote, Palmtree, Sun
} from "lucide-react";
import PageTransition from "../../components/PageTransition";

const CairnsPage = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const services = [
    { icon: Plane, title: "Airport Transfers", desc: "Seamless transfers to and from Cairns Airport (CNS) with meet-and-greet, luggage assistance, and flight monitoring." },
    { icon: Briefcase, title: "Corporate Travel", desc: "Professional transport for business meetings, conferences, and corporate events throughout Cairns and Far North Queensland." },
    { icon: Heart, title: "Weddings & Events", desc: "Elegant wedding car hire for tropical ceremonies at beaches, resorts, and rainforest venues across the region." },
    { icon: Palmtree, title: "Great Barrier Reef Tours", desc: "Chauffeur-driven transfers to reef departure points, Port Douglas, and tropical island connections." },
    { icon: Shield, title: "VIP Secure Transportation", desc: "Discreet, professional service for VIPs and high-profile guests visiting tropical North Queensland." },
    { icon: Wifi, title: "In-Vehicle Wi-Fi", desc: "Stay connected with complimentary high-speed Wi-Fi in all premium vehicles." }
  ];

  const whyChoose = [
    "Cairns-based chauffeurs with extensive local knowledge",
    "Flexible pickup/drop-off across Far North Queensland",
    "Discreet, VIP-level service for all clients",
    "Fleet versatility for individuals and groups (up to 15 pax)",
    "In-vehicle Wi-Fi and multilingual chauffeurs available",
    "Clear, competitive pricing with no hidden fees"
  ];

  const testimonials = [
    { quote: "Cairns corporate travel with Limo Near Me was flawless. Our international guests were impressed.", author: "Regional Manager", company: "Cairns Tourism Board" },
    { quote: "Transport for our group to Port Douglas was seamless and comfortable. Highly recommended.", author: "Event Coordinator", company: "Cairns Convention Centre" },
    { quote: "Professional, punctual, and perfect for our reef tour transfers. Will use again.", author: "Operations Director", company: "Cairns Resort Group" }
  ];

  const faqs = [
    { q: "Do you offer airport pickup at Cairns Airport?", a: "Yes, we provide full meet-and-greet service at Cairns Airport (CNS). Your chauffeur will meet you at arrivals with a name board and assist with luggage." },
    { q: "Can you arrange transfers to Port Douglas?", a: "Absolutely. We offer luxury transfers from Cairns to Port Douglas, Palm Cove, and all Far North Queensland destinations." },
    { q: "Are child seats available?", a: "Yes, we provide infant seats, toddler seats, and booster seats. Please request when booking." },
    { q: "What is the cancellation policy?", a: "Cancellations 24+ hours before pickup receive a full refund. Within 24 hours may incur a 50% charge." },
    { q: "Do you offer Great Barrier Reef transfer packages?", a: "Yes, we can arrange seamless transfers to reef departure points including the Cairns Marina and Port Douglas." }
  ];

  const popularRoutes = [
    { from: "Cairns Airport (CNS)", to: "Cairns CBD", price: "From $88" },
    { from: "Cairns Airport (CNS)", to: "Port Douglas", price: "From $270" },
    { from: "Cairns CBD", to: "Palm Cove", price: "From $110" },
    { from: "Cairns Airport (CNS)", to: "Mission Beach", price: "From $295" },
    { from: "Cairns CBD", to: "Kuranda", price: "From $110" },
    { from: "Cairns Airport (CNS)", to: "Reef Fleet Terminal", price: "From $80" }
  ];

  const suburbs = ["Cairns CBD", "Cairns North", "Palm Cove", "Port Douglas", "Trinity Beach", "Yorkeys Knob", "Kuranda", "Smithfield", "Edge Hill", "Parramatta Park"];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Limo Near Me - Cairns",
    "description": "Cairns luxury chauffeur services for airport transfers, corporate travel, and Great Barrier Reef tours.",
    "address": { "@type": "PostalAddress", "addressLocality": "Cairns", "addressRegion": "QLD", "addressCountry": "AU" },
    "telephone": "+61481437772",
    "url": "https://www.limonearme.com.au/chauffeur/cairns",
    "priceRange": "$$-$$$",
    "openingHours": "Mo-Su 00:00-23:59"
  };

  return (
    <PageTransition>
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Limo Near Me Cairns | Luxury Chauffeur & Airport Transfer Services</title>
        <meta name="description" content="Cairns luxury chauffeur services for airport transfers, corporate travel, and Great Barrier Reef tours. Premium fleet, in-vehicle Wi-Fi, multilingual chauffeurs. Book now." />
        <link rel="canonical" href="https://www.limonearme.com.au/chauffeur/cairns" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2"><img src="/limo-logo.jpg" alt="Limo Near Me Cairns" className="h-12 w-auto" /></Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#services" className="text-muted-foreground hover:text-foreground transition-colors">Services</Link>
              <Link to="/#fleet" className="text-muted-foreground hover:text-foreground transition-colors">Fleet</Link>
              <Link to="/#locations" className="text-muted-foreground hover:text-foreground transition-colors">Locations</Link>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:+61481437772" className="hidden sm:flex items-center gap-2 text-primary"><Phone className="h-4 w-4" /><span className="font-semibold">+61 481 437 772</span></a>
              <Link to="/book"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">Book Now</Button></Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=1920&h=1080&fit=crop')" }} />
        <div className="absolute inset-0 bg-background/85" />
        <div className="absolute inset-0 hero-glow" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="initial" animate="animate" variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6"><MapPin className="h-4 w-4" /><span className="text-sm font-medium">Cairns, Queensland</span></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-foreground mb-6">Chauffeur Services<br /><span className="text-primary">in Cairns</span></h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">Discover luxury transport in tropical Cairns. Our local chauffeurs deliver reliable service for airport transfers, corporate travel, Great Barrier Reef tours, and special occasions.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book"><Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-bold glow-primary">Get Cairns Quote <ChevronRight className="ml-2 h-5 w-5" /></Button></Link>
              <a href="tel:+61481437772"><Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-lg border-black/20 hover:bg-black/5"><Phone className="mr-2 h-5 w-5" /> Call Now</Button></a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">Why Choose Limo Near Me in Cairns?</h2>
              <ul className="space-y-4 mb-8">
                {whyChoose.map((item, i) => (<li key={i} className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" /><span className="text-muted-foreground">{item}</span></li>))}
              </ul>
              <Link to="/book"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">Book Cairns Transfer <ChevronRight className="ml-2 h-5 w-5" /></Button></Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <img src="https://images.unsplash.com/photo-1582967788606-a171c1080cb0?w=600&h=400&fit=crop" alt="Cairns Great Barrier Reef" className="rounded-2xl shadow-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">What We Offer in Cairns</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div key={service.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="dashboard-card group hover:border-primary/50 transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors"><service.icon className="h-7 w-7 text-primary" /></div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{service.title}</h3>
                <p className="text-muted-foreground text-sm">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">What Cairns Clients Say</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="dashboard-card">
                <Quote className="h-8 w-8 text-primary/30 mb-4" />
                <p className="text-muted-foreground italic mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-1 mb-2">{[1,2,3,4,5].map(s => <Star key={s} className="h-4 w-4 text-yellow-500 fill-yellow-500" />)}</div>
                <p className="text-foreground font-semibold">{t.author}</p>
                <p className="text-muted-foreground text-sm">{t.company}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Routes */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Cairns Transfer Routes</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularRoutes.map((route, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="dashboard-card hover:border-primary/50">
                <div className="flex items-center gap-3 mb-3"><div className="w-3 h-3 rounded-full bg-green-500" /><span className="text-foreground font-medium">{route.from}</span></div>
                <div className="flex items-center gap-3 mb-4"><div className="w-3 h-3 rounded-full bg-primary" /><span className="text-foreground font-medium">{route.to}</span></div>
                <div className="flex justify-between items-center pt-3 border-t border-black/10"><Link to="/book"><Button size="sm" variant="outline" className="text-xs rounded-full">Get Price</Button></Link><Link to="/book"><Button size="sm" variant="outline" className="rounded-full">Book</Button></Link></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-card/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Cairns FAQs</h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="dashboard-card">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between text-left">
                  <h3 className="text-foreground font-semibold pr-4">{faq.q}</h3>
                  <ChevronDown className={`h-5 w-5 text-primary transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <p className="text-muted-foreground text-sm mt-4 pt-4 border-t border-black/10">{faq.a}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Book Your Cairns Transfer Today</h2>
          <p className="text-muted-foreground mb-8">Experience tropical luxury with Cairns' premier chauffeur service.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book"><Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-bold glow-primary">Get a Quote</Button></Link>
            <a href="tel:+61481437772"><Button size="lg" variant="outline" className="rounded-full px-10 py-6 text-lg border-primary/50 hover:bg-black/5"><Phone className="mr-2 h-5 w-5" /> +61 481 437 772</Button></a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SeoFooter />
    </div>
    </PageTransition>
  );
};

export default CairnsPage;
